// From the leaf module, NOT from the route components. Importing the superadmin
// predicate from its component closed a cycle that cost a full page reload on
// every edit; see routePaths.ts. This file now imports no component at all,
// which `app-surface-no-component-imports.test.ts` keeps true.
import {
  isBanepadDeviceRoutePath,
  isPasskeyProbeRoutePath,
  isSuperadminRoutePath,
} from './routePaths';

// ─────────────────────────────────────────────────────────────────────
// WHICH SURFACE THE BUNDLE IS SHOWING (RBAC-3 follow-up).
//
// Three surfaces share one bundle and were chosen ONCE, at module load, from
// `window.location.pathname`. That was fine while the only way between them
// was a link: a link is a page load, and a page load picks again.
//
// It stopped being fine when an organization context had to survive the trip.
// The impersonation token lives IN MEMORY, for the reason `lib/auth/clubSession.ts`
// states plainly — a bearer token in web storage is readable by any script
// that gets into the page, and it outlives the tab that earned it. A page load
// therefore destroys the context, which is why «Arbeid som organisasjonen»
// could start a context and then land in the club portal without one.
//
// So the move between surfaces becomes a CLIENT-SIDE transition. Nothing about
// the storage rule changes: a hard reload still ends the context, exactly as a
// reload already returns the club portal to its login screen. That is the same
// rule, applied to one more credential, rather than an exception carved for
// this one.
// ─────────────────────────────────────────────────────────────────────

// `diagnostics` is the WebAuthn probe (#676): no login, no store, no session.
export type Surface = 'device' | 'superadmin' | 'diagnostics' | 'portal';

/** The path each surface is addressed by. `portal` is everything else. */
const PATH: Record<Exclude<Surface, 'portal'>, string> = {
  device: '/pad',
  superadmin: '/sa',
  diagnostics: '/diagnostics/passkeys',
};

/** Which surface a path names. Unchanged from what main.tsx used to decide. */
export function surfaceFor(pathname: string): Surface {
  if (isBanepadDeviceRoutePath(pathname)) return 'device';
  if (isSuperadminRoutePath(pathname)) return 'superadmin';
  if (isPasskeyProbeRoutePath(pathname)) return 'diagnostics';
  return 'portal';
}

export function currentSurface(): Surface {
  return surfaceFor(window.location.pathname);
}

const listeners = new Set<(s: Surface) => void>();

function announce() {
  const surface = currentSurface();
  for (const fn of listeners) fn(surface);
}

/**
 * Subscribe to surface changes, including the browser's Back button.
 *
 * `popstate` is shared with the portal's own `useNavigation`, which pushes
 * `#view` entries. The two do not collide: this one reads the PATH and that
 * one reads the HASH, and whichever surface is mounted is the only one with a
 * listener attached.
 */
export function subscribeSurface(fn: (s: Surface) => void): () => void {
  listeners.add(fn);
  if (listeners.size === 1) window.addEventListener('popstate', announce);
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0) window.removeEventListener('popstate', announce);
  };
}

/**
 * Move to another surface WITHOUT reloading the page.
 *
 * The whole point: the JavaScript context survives, so anything held in memory
 * — the impersonation token above all — is still there on the other side.
 *
 * Falls back to a real navigation if the History API is unavailable. The
 * context is lost in that case, which is the old behaviour and still correct:
 * losing it is safe, and silently continuing without one would not be.
 */
export function navigateToSurface(surface: Surface): void {
  const path = surface === 'portal' ? '/' : PATH[surface];
  try {
    window.history.pushState({ surface }, '', path);
    announce();
  } catch {
    window.location.assign(path);
  }
}
